import { useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  setInboxUnreadCount,
  setLeadInboxUnreadCount,
  setTicketUnreadCount,
  pushInboxEvent,
  isLoggedIn,
} from "../../store/auth/authSlice";
import { EMAIL_API_URL } from "../../config";
import { authHeaders, getSessionToken, COOKIE_SESSION } from "../utils/session";

/* Always absolute, never a relative "/api/..." path: on the dev server a
   relative URL resolves against localhost and 404s, and in production it would
   hit whichever host serves the CMS rather than the API. The host comes from
   config so staging and production differ in one place. */
const BASE = `${EMAIL_API_URL}/api`;

/* One connection for the whole app. Held at module level so a second mount —
   a remount, a second component calling this hook — reuses it instead of
   opening another stream. */
let sharedStream = null;

export function useInboxBadges() {
  const dispatch = useDispatch();
  /* The stream opens when there is a session, and a session can appear after
     this hook first runs — signing in does not reload the page. Watching the
     login flag is what re-runs the effect at that moment; with an empty
     dependency list it bailed out once and stayed closed for the rest of the
     session, so live updates only worked after a manual refresh. */
  const loggedIn = useSelector(isLoggedIn);

  const authHdr = useCallback(
    () => ({ ...authHeaders() }),
    [],
  );

  const refetchSub = useCallback(async () => {
    try {
      const internalUserId = Number(localStorage.getItem("trade_user_id"));
      const r = await axios.get(`${BASE}/subscription-inbox/unread-count`, {
        params: { internal_user_id: internalUserId },
        headers: authHdr(),
      });
      dispatch(setInboxUnreadCount(r.data?.unread_count ?? r.data?.count ?? 0));
    } catch {}
  }, [dispatch, authHdr]);

  const refetchLead = useCallback(async () => {
    try {
      /* No owner param: the lead inbox is shared, so the badge counts every
         unread reply the token is allowed to see. Scoping it to one user would
         hide a manager's team replies from their own badge. */
      const r = await axios.get(`${BASE}/lead-inbox/unread-count`, {
        headers: authHdr(),
      });
      dispatch(setLeadInboxUnreadCount(r.data?.unread_count ?? r.data?.count ?? 0));
    } catch {}
  }, [dispatch, authHdr]);

  const refetchTicket = useCallback(async () => {
    try {
      const internalUserId = Number(localStorage.getItem("trade_user_id"));
      const r = await axios.get(`${BASE}/single-email/unread-count`, {
        params: { internalUserId },
        headers: authHdr(),
      });
      dispatch(setTicketUnreadCount(r.data?.count ?? 0));
    } catch {}
  }, [dispatch, authHdr]);

  /* The cleanup below runs with the values the effect was set up with, so a
     cleanup triggered by signing out would still see `loggedIn === true` and
     leave the stream open for the next person to sign in on this browser. A ref
     assigned during render already holds the new value by the time the cleanup
     runs, so it is what the cleanup asks. */
  const loggedInRef = useRef(loggedIn);
  loggedInRef.current = loggedIn;

  const refetchAll = useCallback(() => {
    refetchSub();
    refetchLead();
    refetchTicket();
  }, [refetchSub, refetchLead, refetchTicket]);

  useEffect(() => {
    /* Gated on being signed in, and on nothing else. This used to also require
       hasSession(), which reads a marker cookie the server may never set — when
       it did not, the stream was never opened at all and the server saw 0
       clients. The stream is authorised by the httpOnly cookie the browser
       sends with it, so a marker is not what decides whether to try. */
    if (!loggedIn) return undefined;

    /* Seed first, then connect. Replies that arrived before the stream was
       open are never pushed — they are already in the database, so the initial
       counts call is what brings them in. The two together give the full
       picture. */
    refetchAll();

    if (!sharedStream) {
      /* EventSource cannot set headers, which is why this endpoint has always
         taken its credential in the query string. Once the session is an
         httpOnly cookie there is no string to pass — the browser carries it
         instead, but only if the stream is opened with credentials. */
      const token = getSessionToken();
      sharedStream = COOKIE_SESSION
        ? new EventSource(`${EMAIL_API_URL}/api/events/stream`, { withCredentials: true })
        : new EventSource(
          `${EMAIL_API_URL}/api/events/stream?token=${encodeURIComponent(token)}`,
        );
    }

    const es = sharedStream;

    /* One line per state change, so "is the live stream up?" is answerable from
       the console instead of guessed at. A stream that never opens and a stream
       that opens but is sent nothing look identical from the UI, and telling
       them apart is the difference between a frontend bug and a backend one. */
    es.onopen = () => console.info("[inbox] live stream connected");

    const handle = (raw) => {
      let ev;
      try { ev = JSON.parse(raw); } catch { return; }
      // The opening handshake, and the 25s keep-alive, carry nothing to act on.
      if (ev.type !== "inbound") return;
      // Fan out to all subscribers via Redux — screens react via
      // useSelector(selectLastInboxEvent)
      dispatch(pushInboxEvent(ev));
      // Also bump the matching badge count immediately
      if (ev.inbox === "subscription") refetchSub();
      else if (ev.inbox === "lead") refetchLead();
      else if (ev.inbox === "ticket") refetchTicket();
    };

    /* `onmessage` only receives frames the server sent with no `event:` line.
       The moment one is named — `event: inbound` — it is delivered to a
       listener of that name instead and `onmessage` never fires at all, which
       looks exactly like a working connection that delivers nothing. Both are
       handled so the client does not depend on which form the server chose. */
    es.onmessage = (e) => handle(e.data);

    const NAMED = ["inbound", "lead", "ticket", "subscription", "notification"];
    const namedHandler = (e) => handle(e.data);
    NAMED.forEach((name) => es.addEventListener(name, namedHandler));

    // The browser reconnects on its own — the server sends its own retry
    // interval — so there is nothing to do here and no retry loop to write.
    es.onerror = () => {};

    const onFocus = () => refetchAll();
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("focus", onFocus);
      NAMED.forEach((name) => es.removeEventListener(name, namedHandler));
      /* Closed on sign-out only. The stream belongs to the session, so closing
         it on every remount would reopen one per navigation. */
      if (!loggedInRef.current && sharedStream) {
        sharedStream.close();
        sharedStream = null;
      }
    };
  }, [loggedIn]); // eslint-disable-line react-hooks/exhaustive-deps
}
